<template>
  <v-dialog :fullscreen="$vuetify.breakpoint.smAndDown" :value="showSurveyDialog" max-width="800" persistent
            @click:outside="postpone">
    <v-card v-if="loading">
      <v-skeleton-loader type="card heading" />
    </v-card>
    <v-card v-else-if="currentSurvey">
      <v-form ref="form" v-model="valid" lazy-validation @submit.prevent="save">
        <v-card-title>
          <div class="info--text">
            {{currentSurvey.name}}
          </div>
          <v-spacer/>
          <v-btn :disabled="loading" icon @click="postpone" v-if="currentSurvey?.isPostponable">
            <v-icon>
              mdi-close
            </v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle v-if="currentSurvey.desc">
          {{currentSurvey.desc}}
        </v-card-subtitle>
        <v-card-text class="scroll-dialog pt-2">
          <v-stepper vertical v-model="step">
            <!-- Dynamische Steps -->
            <template v-for="(item, i) in currentSurvey.items">
              <!-- Step-Header -->
              <v-stepper-step
                  color="info"
                  :step="i + 1"
                  :key="'step-' + i"
                  :complete="step > i + 1"
              >
                {{ getStepName(item.type) }}
              </v-stepper-step>

              <!-- Step-Content -->
              <v-stepper-content
                  :step="i + 1"
                  :key="'content-' + i"
              >
                <v-card-text class="py-0">
                  <component
                      :additional-data="additionalData[i]"
                      @responseItem="setResponseItem"
                      :component="getComponent(item.type)"
                      :is="getComponent(item.type)"
                      :item="item"
                      :survey="currentSurvey"
                      :step.sync="step"
                      :max-steps="currentSurvey.items.length"
                  />
                </v-card-text>
              </v-stepper-content>
            </template>

            <!-- Abschluss Step -->
            <v-stepper-step
                color="info"
                :step="currentSurvey.items.length + 1"
                :key="'step-' + (currentSurvey.items.length + 1)"
                :complete="step > currentSurvey.items.length + 1"
            >
              Abschluss
            </v-stepper-step>

            <v-stepper-content
                :step="currentSurvey.items.length + 1"
                :key="'content-' + (currentSurvey.items.length + 1)"
            >
              <v-card-text class="py-0">
                <v-alert type="info" class="mb-4">
                  Vielen Dank für deine Teilnahme an der Umfrage!
                </v-alert>
                <v-btn class="my-2" depressed block v-if="step > 1" @click="step--" >
                  Zurück
                </v-btn>
                <v-btn
                    class="my-2"
                    block
                    color="success"
                    @click="save()"
                    :loading="saveLoading"
                    :disabled="!valid"
                >
                  Umfrage abschließen
                </v-btn>
              </v-card-text>
            </v-stepper-content>
          </v-stepper>
        </v-card-text>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import Vue from "vue";
import store from "@/store";
import {SurveyItemType} from "@/enums/survey-manager/SurveyItemType.enum";
import WeekdaysRangeSurveyInput from "@/components/app/userSurvey/weekdaysRangeSurveyInput.vue";
import EventSelectionSurveyInput from "@/components/app/userSurvey/eventSelectionSurveyInput.vue";
import AssignedInstitutionSurveyInput from "@/components/app/userSurvey/assignedInstitutionSurveyInput.vue";
import {
  AssignedInstitutionsSelectionItem,
  CreateUserSurveyResponseRequestDto, EventSelectionItem, WeekDaysRangeItem,
} from "@/classes/dto/survey-manager/request/CreateUserSurveyResponse.request.dto";
import {SurveyManagerApi} from '@/classes/api/survey-manager.api.class';
import {GetSurveyDataResponseDto} from "@/classes/dto/survey-manager/response/GetSurveyData.response.dto";

export default Vue.extend({
  components: {
    WeekdaysRangeSurveyInput,
    EventSelectionSurveyInput,
    AssignedInstitutionSurveyInput,
  },
  data: () => ({
    valid: false,
    loading: false,
    step: 1,
    responseItems: [] as Array<WeekDaysRangeItem | EventSelectionItem | AssignedInstitutionsSelectionItem>,
    additionalData: [] as GetSurveyDataResponseDto['items'],
    saveLoading: false,
  }),
  computed: {
    currentSurvey() {
      return store.state.currentSurvey;
    },
    showSurveyDialog() {
      if (!store.state.currentSurvey) {
        return false;
      }
      return store.state.showSurveyDialog;
    },
  },
  methods: {
    async getAdditionalData() {
      const surveyId = this.currentSurvey?._id;
      if (!surveyId) {
        return;
      }
      this.loading = true;
      try {
        const resp = await SurveyManagerApi.dataProvider.getSurveyData({ surveyId });
        if (resp) {
          this.additionalData = resp.items;
        }
      } catch (e) {
        this.$$showSnackbar('Fehler beim Laden der Umfragedaten', 'error', e);
      } finally {
        this.loading = false;
      }

    },
    getStepName(type: SurveyItemType) {
      return SurveyItemType.Record[type].text;
    },
    getComponent(type: SurveyItemType) {
      switch (type) {
        case SurveyItemType.WEEK_DAYS_RANGE:
          return WeekdaysRangeSurveyInput;
        case SurveyItemType.EVENT_SELECTION:
          return EventSelectionSurveyInput;
        case SurveyItemType.ASSIGNED_INSTITUTIONS_SELECTION:
          return AssignedInstitutionSurveyInput;
        default:
          // do nothing
      }
    },
    async postpone() {
      if (this.currentSurvey?.isPostponable) {
        try {
          await SurveyManagerApi.response.createSurveyResponse({
            postponed: true,
            survey: this.currentSurvey._id,
          });
          this.reset();
        } catch (e) {
          this.$$showSnackbar('Fehler beim Schließen der Umfrage', 'error', e);
        }
      }
    },
    reset() {
      store.commit('showSurveyDialog', {value: false, currentSurvey: undefined});
    },
    setResponseItem(item: WeekDaysRangeItem | EventSelectionItem | AssignedInstitutionsSelectionItem) {
      const found = this.responseItems.find((i) => i.type === item.type);
      if (found) {
        const index = this.responseItems.indexOf(found);
        this.responseItems[index] = item;
      } else {
        this.responseItems.push(item);
      }
    },
    async save() {
      if (!this.currentSurvey) {
        return;
      }
      const  request = {} as CreateUserSurveyResponseRequestDto;
      request.survey = this.currentSurvey._id;
      request.items = this.responseItems;
      try {
        await SurveyManagerApi.response.createSurveyResponse(request);
        this.$store.commit('showSurveyDialog', {value: false, currentSurvey: undefined});
      } catch (e) {
        this.$$showSnackbar('Fehler beim Speichern der Umfrage', 'error', e);
      } finally {
        this.saveLoading = false;
      }
    },
  },
  watch: {
    showSurveyDialog: {
      immediate: true,
      handler(val) {
        if (val) {
          this.getAdditionalData();
        } else {
          this.reset();
        }
      },
    },
  },
});
</script>
